<template>
	<v-dialog
		v-model="localShowDialog"
		@click:outside="closeDialog"
		@keydown.esc="closeDialog"
		:width="$vuetify.display.mobile ? '98%' : '500px'"
	>
		<v-card data-test="data-source-list-dialog">
			<v-card-title class="dialog-header sgnl-subtitle-medium"
				>Data Sources
				<v-spacer />
				<v-btn
					icon
					variant="text"
					size="small"
					@click="closeDialog"
				>
					<Close16 />
				</v-btn>
			</v-card-title>
			<v-divider />
			<v-card-text class="dialog-body">
				<LoadingCard
					v-if="loadingSources"
					loadingText="Loading Data Sources"
				/>
				<template v-else>
					<div
						v-if="dataSourceList.length > 0"
						class="data-source-picker-list"
					>
						<DataSourcePickerCard
							v-for="dataSource in dataSourceList"
							:key="dataSource.ResourceID"
							:dataSourceID="dataSource.ResourceID"
							:dataSourceType="dataSource.ResourceType"
							@set-data-source="setDataSource"
						/>
					</div>
					<template v-else>
						<v-alert
							density="compact"
							variant="outlined"
							type="warning"
							border="start"
							class="sgnl-paragraph-s-regular"
						>
							<template #prepend>
								<ConnectionTwoWay16 class="alert-warning-icon" />
							</template>
							No Data sources or Data Drives found! Please use the
							<router-link
								:to="{ name: 'DataSources' }"
								class="sgnl-link"
								>Data and Sensors</router-link
							>
							page to connect to data sources and create Data Drives.
						</v-alert>
					</template>
				</template>
			</v-card-text>
		</v-card>
	</v-dialog>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import Close16 from "@carbon/icons-vue/es/close/16";
import ConnectionTwoWay16 from "@carbon/icons-vue/es/connection--two-way/16";
import { sdk } from "@/js/sdk";
import DataSourcePickerCard from "@/components/DataSources/DataSourcePickerCard.vue";
import LoadingCard from "@/components/LoadingCard.vue";
import { useDataSourcesStore } from "@/stores/dataSources";
import { useUserStore } from "@/stores/user";
import { DataSourceTypeE, MountableDataSource } from "@/types/api/dataSources";
import { monitoringCaptureError } from "@/plugins/monitoring";

type ComponentData = {
	gatewayList: MountableDataSource[];
	bucketList: MountableDataSource[];
	driveList: MountableDataSource[];
	signaloidCloudStorage: MountableDataSource[];
	loadingGateways: boolean;
	loadingBuckets: boolean;
	loadingDrives: boolean;
};

export default defineComponent({
	name: "DataSourceListDialog",
	components: { DataSourcePickerCard, LoadingCard, Close16, ConnectionTwoWay16 },
	props: {
		showDialog: {
			type: Boolean,
			default: false,
		},
		dataSourceExcludeList: {
			type: Array,
			default: () => [],
		},
	},
	emits: {
		"update:showDialog": (_data: boolean) => true,
		"close-dialog": () => true,
		"set-data-source": (_data: any) => true,
	},
	data: (): ComponentData => ({
		gatewayList: [],
		bucketList: [],
		driveList: [],
		signaloidCloudStorage: [],
		loadingGateways: false,
		loadingBuckets: false,
		loadingDrives: false,
	}),
	computed: {
		localShowDialog: {
			get() {
				return this.showDialog;
			},
			set(value) {
				this.$emit("update:showDialog", value);
			},
		},
		unfilteredSourceList(): MountableDataSource[] {
			return [...this.gatewayList, ...this.bucketList, ...this.driveList, ...this.signaloidCloudStorage];
		},
		dataSourceList(): any {
			return this.unfilteredSourceList.filter((x) => {
				return !this.isInExcludeList(x);
			});
		},
		loadingSources(): boolean {
			return this.loadingGateways || this.loadingBuckets || this.loadingDrives;
		},
	},
	setup() {
		// Note: gets called after beforeCreate in vue2
		const dataSourcesStore = useDataSourcesStore();
		const userStore = useUserStore();
		return { dataSourcesStore, userStore };
	},
	methods: {
		closeDialog() {
			this.$emit("close-dialog");
		},
		setDataSource(dataSource: MountableDataSource) {
			this.$emit("set-data-source", dataSource);
			this.closeDialog();
		},
		async fetchDataSources() {
			// FIXME: parallise
			// * get gateways
			try {
				this.loadingGateways = true;

				//get things
				const userThings = await sdk.things.list();
				this.gatewayList = userThings.ThingIDs.map((thingID: string): MountableDataSource => {
					return {
						ResourceID: thingID,
						ResourceType: DataSourceTypeE.Gateway,
					};
				});
			} catch (error) {
				monitoringCaptureError(error, "Fetch data source list");
			} finally {
				this.loadingGateways = false;
			}

			// * get buckets
			try {
				this.loadingBuckets = true;

				//get buckets
				const userBuckets = await sdk.buckets.list();
				this.bucketList = userBuckets.bucket_ids.map((bucketID: string): MountableDataSource => {
					return {
						ResourceID: bucketID,
						ResourceType: DataSourceTypeE.Bucket,
					};
				});
			} catch (error) {
				monitoringCaptureError(error, "Fetch data source list");
			} finally {
				this.loadingBuckets = false;
			}

			// * get drive list
			try {
				this.loadingDrives = true;

				//get drives
				const userDrives = await sdk.drives.list();
				this.dataSourcesStore.setDriveList(userDrives.drive_ids);
				// FIXME: this could potentially be done in the store
				this.driveList = userDrives.drive_ids.map((driveID: string): MountableDataSource => {
					return {
						ResourceID: driveID,
						ResourceType: DataSourceTypeE.Drive,
					};
				});
			} catch (error) {
				monitoringCaptureError(error, "Fetch data source list");
			} finally {
				this.loadingDrives = false;
			}
			// * get signaloid cloud storage list
			try {
				const userID = await this.userStore.getCurrentUserID();

				this.signaloidCloudStorage = [
					{
						ResourceID: `signaloid-cloud-storage:/${userID}`,
						ResourceType: "SignaloidCloudStorage",
					},
				];
			} catch (error) {
				monitoringCaptureError(error, "Fetch data source list");
			}
		},
		isInExcludeList(mountableDataSource: MountableDataSource) {
			return this.dataSourceExcludeList.some((excludeID) => {
				return excludeID == mountableDataSource.ResourceID;
			});
		},
	},

	mounted() {
		this.fetchDataSources();
	},
});
</script>

<style lang="scss" scoped>
.alert-warning-icon {
	width: var(--sgnl-icon-16);
	height: var(--sgnl-icon-16);
	color: var(--sgnl-alert-warning-icon);
}
.data-source-picker-list {
	display: flex;
	flex-direction: column;
	gap: var(--sgnl-space-16);
}
</style>
